// Lib
import { optionsPrices } from '../helpers';
import { TUpdateData } from 'pages/Thesaurus/reducer';

type TUpdatedDataByKey = {
	id?: string;
	key?: string;
	data?: Compound[];
	value?: string | number;
};

type typePriceSelect = {
	mg?: string;
};

export type TCompoundPrice = {
	mg: number;
	priceUsd: number;
	priceEur: number;
	available: boolean;
};

export type TCurrentPrice = {
	mg: number;
	type: string;
	value: number;
	price: number;
};

export type TCompoundPriceEntry = {
	supplier: string;
	prices: TCompoundPrice[];
};

export type TSupplierId = {
	supId: string;
	supplier: string;
};

export type TDescriptor = {
	qed: number;
	hbd?: number;
	wqed: number;
	logP: number;
	tpsa: number;
	fsp3: number;
	heavyAtomCount: number;
	molecularWeight: number;
	rotatableBondCount: number;
};

export type Compound = {
	id: string;
	px?: number;
	doi?: string;
	cas?: string;
	name?: string;
	zId?: string;
	dbId?: string;
	title?: string;
	action?: string;
	target?: string;
	purity?: number;
	cxSmiles?: string;
	onStock: boolean;
	iupacName: string;
	description: string;
	supIds?: TSupplierId[];
	descriptor?: TDescriptor;
	prices: TCompoundPriceEntry[];

	isPOA?: boolean;
	quantity?: number;
	selected?: boolean;
	currentTypePrice?: string;
	currentPrice?: TCurrentPrice;
};

type AccessibilityFilter = 'ON_STOCK' | 'ON_STOCK_POA' | 'OUT_OF_STOCK' | 'ALL';

export const convertToPriceSelect = (item: typePriceSelect) => {
	return {
		value: 'poa',
		mg: item.mg || '',
		label: `POA/${item.mg}mg`
	};
};

export function filterByAccessibility(
	compounds: Compound[],
	filter: AccessibilityFilter
): Compound[] {
	return compounds.filter(compound => {
		const { onStock, prices } = compound;
		const hasAvailablePrice = prices?.[0]?.prices?.some(
			price => price.available
		);

		switch (filter) {
			case 'ON_STOCK':
				return onStock && hasAvailablePrice;

			case 'ON_STOCK_POA':
				return onStock && !hasAvailablePrice;

			case 'OUT_OF_STOCK':
				return !onStock;

			case 'ALL':
			default:
				return true;
		}
	});
}

export const priceAvailable = (item: Compound) => {
	return item?.prices[0].prices?.find(
		(price) => price.mg === item?.currentPrice?.mg
	);
};

export const convertOptions = (data: TCompoundPrice[]) => data?.length
	? data?.map((item) =>
		optionsPrices(item)
	) : [];

export const updateListWithPrice = (list: Compound[], prepackage: any, checkboxAll?: boolean) =>
	list.map((item) => {
		const updatedItem = { ...item, currentPrice: prepackage };
		const canSelect = !updatedItem.isPOA &&
			updatedItem.onStock &&
			priceAvailable(updatedItem)?.available;

		return {
			...updatedItem,
			currentPrice: prepackage,
			selected: checkboxAll && canSelect
		};
	});

export const updatedDataByKey = (props: TUpdatedDataByKey) => {
	const {
		id = '',
		key = '',
		value,
		data = [],
	} = props;

	return data?.length ? data.map((item: Compound) => {
		if (item.id === id) {
			return {
				...item,
				[key]: key === 'selected' ? !item.selected : value
			};
		}
		return item;
	}) : [];
};

export const checkAllCheckbox = (data: Compound[]) => {
	const validItems = data.filter((item) => {
		if (!item.onStock || item.isPOA) return false;

		const currentMg = item.currentPrice?.mg;
		if (currentMg === undefined) return false;

		const supplierPrices = item?.prices?.find(
			(priceGroup) => priceGroup.supplier === item.currentTypePrice
		)?.prices;

		return supplierPrices?.some(
			(price) => price.mg === currentMg && price.available
		);
	});

	return (
		validItems.length > 0 &&
		validItems.every((item) => item.selected)
	);
};

export const updateData = (props: TUpdateData) => {
	const {
		id,
		key,
		data,
		value
	} = props;
	return data?.map((item) => {
		if (item.id === id) {
			return {
				...item,
				[key as string]: value
			};
		}

		return item;
	});
};